const NEXT_CHUNK_URL_PATTERN = /https?:\/\/[^)\s]+\/_next\/static\/chunks\/([^\s):]+\.js)(:\d+:\d+)?/g;
const TURBOPACK_HASH_SUFFIX_PATTERN = /_[a-z0-9]+\._$/;
const EVAL_ANONYMOUS_FRAME_PATTERN = /\(eval at ([^()]+) \([^)]+\), <anonymous>:(\d+):(\d+)\)/g;
const PLAY_MODE_STACK_TRIM_ENABLED = true;
const PLAY_MODE_STACK_TRIM_ANCHOR_PATTERN = /\bCustomGameRuntime\.startGame\b/;
const STACK_FRAME_PATTERN = /^\s*at\s+/;

function toReadableChunkPath(chunkFileName: string): string {
  const withoutExtension = chunkFileName.replace(/\.js$/, '');
  const withoutHashSuffix = withoutExtension.replace(TURBOPACK_HASH_SUFFIX_PATTERN, '');
  const withPackagePrefix = withoutHashSuffix
    .replace(/^packages_engine_dist_src_/, 'packages/engine/src/')
    .replace(/^packages_editor_src_/, 'packages/editor/src/')
    .replace(/^packages_sdk_dist_src_/, 'packages/sdk/src/')
    .replace(/^packages_agent_dist_src_/, 'packages/agent/src/');

  if (!withPackagePrefix.includes('/')) {
    return chunkFileName;
  }

  return `${withPackagePrefix.replace(/_/g, '/')}.js`;
}

/**
 * Normalises noisy stack traces from Next.js/Turbopack play mode output.
 */
export function cleanStackTrace(stackOrMessage: string): string {
  const trimmed = stackOrMessage.trim();
  if (trimmed.length === 0) {
    return '';
  }

  const withReadableChunkPaths = trimmed.replace(
    NEXT_CHUNK_URL_PATTERN,
    (_match, chunkFileName: string, position: string | undefined) => {
      const readablePath = toReadableChunkPath(chunkFileName);
      return `${readablePath}${position ?? ''}`;
    },
  );

  const normalisedEvalFrame = withReadableChunkPaths.replace(
    EVAL_ANONYMOUS_FRAME_PATTERN,
    '(<game-bundle> eval at $1:$2:$3)',
  );

  return trimPlayModeStackTail(normalisedEvalFrame);
}

function trimPlayModeStackTail(stackOrMessage: string): string {
  if (!PLAY_MODE_STACK_TRIM_ENABLED || !isStackLikeMessage(stackOrMessage)) {
    return stackOrMessage;
  }

  const lines = stackOrMessage.split(/\r?\n/);
  const anchorIndex = lines.findIndex(line => PLAY_MODE_STACK_TRIM_ANCHOR_PATTERN.test(line));
  if (anchorIndex === -1) {
    return stackOrMessage;
  }

  const trailingLines = lines.slice(anchorIndex + 1);
  const trailingNonEmptyLines = trailingLines.filter(line => line.trim().length > 0);
  if (trailingNonEmptyLines.length === 0) {
    return stackOrMessage;
  }

  const hasOnlyStackFramesBelowAnchor = trailingNonEmptyLines.every(line => STACK_FRAME_PATTERN.test(line));
  if (!hasOnlyStackFramesBelowAnchor) {
    return stackOrMessage;
  }

  return lines.slice(0, anchorIndex + 1).join('\n');
}

function isStackLikeMessage(message: string): boolean {
  return message.split(/\r?\n/).some(line => STACK_FRAME_PATTERN.test(line));
}
